import React, { useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import { BarChart, Globe, Heart, Users } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import Particle from "../Particle";

export default function Stats() {
  const navigate = useNavigate();
  const location = useLocation();


  const handleClick = () => {
    navigate("/registration#internship-section");
  };

  const handleClick_2 = () => {
    window.open(
      "https://docs.google.com/forms/d/1iwEzu53wDAe41RjEDe-gHIX9H61QikSfR2RKmyCBp_M/edit",
      "_blank",
      "noopener,noreferrer"
    );
  };
  const handleClick_3 = () => {
    window.open(
      "https://docs.google.com/forms/d/1Q2Lc15IwomBIFeSAxIfsK56xI3vkCjvKW8yXQcWQvmM/edit",
      "_blank",
      "noopener,noreferrer"
    );
  };

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const headlineStats = [
    { label: "Youth trained", value: "100+", icon: Users },
    { label: "Seniors served", value: "50+", icon: Heart },
    { label: "Counties & Cities", value: "4 counties · 8 cities", icon: Globe },
    { label: "Ambassador initiatives", value: "10 launched", icon: BarChart },
  ];

  const surveyStats = [
    { stat: "92%", caption: "Interns reported improved confidence & leadership skills" },
    { stat: "9/10", caption: "Participants said the program changed how they think about their future in tech" },
    { stat: "100%", caption: "Interns improved teamwork & problem-solving abilities" },
    { stat: "80%+", caption: "Built portfolios or pursued internships/jobs after the program" },
  ];

  const byTheNumbers = [
    { label: "Interns (Summer 2025)", value: "12" },
    { label: "Collaborative apps built", value: "10" },
    { label: "Rated Excellent/Good", value: "85%" },
    { label: "Would recommend", value: "100%" },
  ];

  const voices = [
    {
      name: "Zarya",
      quote:
        "This internship made me consider how to develop applications more ethically so they could have more widespread use in communities.",
    },
    { name: "Liyah", quote: "I loved building the websites and apps. It allowed me to find my passion in web design again." },
    { name: "Shogufta", quote: "At first GitHub was confusing, but now I can push, pull, and commit with confidence." },
    {
      name: "Kavi",
      quote:
        "I have been an intern for Choicest Fruit for 2 years and I have learned a lot about the rapid development of technology and how to use it. I have gained skills in basic website development including understanding html, css and javascript.",
    },
  ];

  return (
    <Container fluid className="about-section impact-page impact-stats-page">
      <Particle />
      <Container className="impact-container impact-stats-container">
        <header className="impact-hero">
          <h1 className="impact-hero-title">
            Our <strong className="purple">Impact</strong>
          </h1>
          <p className="impact-hero-lead">
            Since 2024 we’ve centered our work in the City of Austin and across Hays and Travis Counties, Texas—closing equity
            gaps in tech education by equipping youth with hands-on coding, robotics, organic urban gardening, and ethical
            technology training, plus mentorship and pathways from Learners → Ambassadors → Innovators → Entrepreneurs. Our
            programs also serve seniors, schools, and tech companies—ensuring technology benefits all.
          </p>
        </header>

        <Row className="impact-stats-top" style={{ justifyContent: "center" }}>
          <Col md={6} className="impact-stats-col">
            <div className="policy-card impact-stats-card">
              <h2 className="impact-card-title">Headline Impact</h2>
              <p className="about-text impact-card-text">
                Representation gaps persist in tech. We work locally and internationally to change that through hands-on
                learning, mentorship, and real-world projects.
              </p>

              <div className="impact-stats-grid" role="list" aria-label="Headline impact statistics">
                {headlineStats.map((h) => (
                  <div key={h.label} className="impact-stat-tile" role="listitem">
                    <div className="impact-stat-icon" aria-hidden="true">
                      {h.icon ? React.createElement(h.icon, { size: 26 }) : null}
                    </div>
                    <div className="impact-stat-value">{h.value}</div>
                    <div className="impact-stat-label">{h.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </Col>

          <Col md={6} className="impact-stats-col">
            <div className="policy-card impact-stats-card">
              <h2 className="impact-card-title">Survey Highlights</h2>
              <p className="about-text impact-card-text">Participant surveys showing measurable growth.</p>

              <div className="impact-survey-grid" role="list" aria-label="Survey highlights">
                {surveyStats.map((s) => (
                  <Card key={s.stat} className="policy-card impact-mini-card" role="listitem">
                    <Card.Body>
                      <div className="impact-mini-stat">{s.stat}</div>
                      <div className="impact-mini-caption">{s.caption}</div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </div>
          </Col>
        </Row>

        <section className="impact-section" aria-label="Participant voices">
          <h2 className="impact-section-heading">Participant Voices</h2>
          <div className="impact-voices-grid" role="list">
            {voices.map((v) => (
              <div key={v.name} className="policy-card impact-voice-card" role="listitem">
                <p className="impact-voice-quote">“{v.quote}”</p>
                <p className="impact-voice-name">— {v.name}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="impact-section" aria-label="By the numbers">
          <Row className="impact-stats-bottom" style={{ justifyContent: "center" }}>
            <Col lg={8} className="impact-stats-col">
              <div className="policy-card impact-stats-card">
                <h2 className="impact-card-title">By the Numbers (Summer 2025 Internship)</h2>
                <p className="about-text impact-card-text">
                  A snapshot of our recent cohort showing program outputs and participant outcomes.
                </p>

                <div className="impact-byn-grid" role="list" aria-label="By the numbers">
                  {byTheNumbers.map((b) => (
                    <div key={b.label} className="impact-byn-tile" role="listitem">
                      <div className="impact-byn-value">{b.value}</div>
                      <div className="impact-byn-label">{b.label}</div>
                    </div>
                  ))}
                </div>

                <div className="impact-projects">
                  <h3 className="impact-subheading">Intern Projects</h3>
                  <p className="about-text impact-body">
                    Interns built collaborative apps including Crop Companion Finder, Breathing & Meditation App, and utility
                    tools focused on local problems. Projects emphasized design, accessibility, and ethical technology.
                  </p>
                  <Button onClick={handleClick} className="custom-yellow-btn impact-apply-btn">
                    Apply for Internship
                  </Button>
                </div>
              </div>
            </Col>

            <Col lg={4} className="impact-stats-col">
              <div className="policy-card impact-stats-card">
                <h3 className="impact-card-title">Our Reach So Far</h3>
                <ul className="impact-reach">
                  <li>100+ youth trained in coding, robotics, and ethical tech</li>
                  <li>50+ seniors served in digital literacy</li>
                  <li>4 counties & 8 cities impacted</li>
                  <li>10+ youth-led initiatives launched</li>
                  <li>10+ partnerships</li>
                </ul>
              </div>
            </Col>
          </Row>
        </section>

        <section className="impact-section" aria-label="Partner with us">
          <div className="policy-card impact-partner-cta">
            <div className="impact-partner-left">
              <h3 className="impact-card-title">Partner with us</h3>
              <p className="about-text impact-card-text">
                Support programs, mentorship, or offer project opportunities—help us scale local impact and diversify tech
                pathways.
              </p>
            </div>
            <div className="impact-partner-right">
              <Button onClick={handleClick_2} className="custom-yellow-btn impact-apply-btn">
                Become a Partner
              </Button>
              <Button onClick={handleClick_3} className="custom-yellow-btn impact-apply-btn impact-apply-btn-secondary">
                Volunteer / Mentor
              </Button>
            </div>
          </div>
        </section>

        <footer className="impact-stats-footer">
          Raising awareness on ethical & responsible technology through our YouTube channel and online content.
        </footer>
      </Container>
    </Container>
  );
}
